import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight, MapPin, Users } from "lucide-react";
import { CORPORATE_HOMES } from "@/lib/constants";

function ProductCard({ home, index }) {
  const href = `/firmaboliger/${home.slug}`;
  return (
    <motion.article
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className="group snap-start shrink-0 w-[82%] sm:w-[calc(50%-0.75rem)] lg:w-[calc(25%-1.5rem)] flex flex-col bg-white border border-black/[0.06] hover:border-[#D4AF37]/50 hover:shadow-[0_24px_60px_-28px_rgba(17,17,17,0.35)] transition-[border-color,box-shadow] duration-500"
      data-testid={`stay-card-${home.slug}`}
    >
      <Link to={href} className="block relative" data-testid={`stay-card-link-${home.slug}`}>
        <div className="relative aspect-[4/5] overflow-hidden bg-[#F1EFEA]">
          <img
            src={home.coverImage}
            alt={home.shortTitle}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.05]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#111111]/45 via-transparent to-transparent opacity-80 group-hover:opacity-100 transition-opacity duration-700" />
          <div className="absolute top-3 left-3 bg-white/95 text-[#111111] text-[9px] tracking-[0.28em] uppercase px-3 py-1.5">
            Firmabolig
          </div>
          <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between text-white">
            <span className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.12em] uppercase">
              <MapPin size={12} className="text-[#D4AF37]" strokeWidth={1.6} />
              <span data-testid={`stay-location-${home.slug}`}>{home.area}</span>
            </span>
            {home.sqm && (
              <span className="text-[11px] tracking-[0.12em] uppercase">ca. {home.sqm} m²</span>
            )}
          </div>
        </div>
      </Link>

      <div className="px-5 sm:px-6 pt-6 pb-6 flex flex-col flex-1 text-center">
        <Link
          to={href}
          className="font-serif text-[1.35rem] sm:text-2xl text-[#111111] font-light leading-tight tracking-tight hover:text-[#C9A227] transition-colors"
          style={{ fontFamily: "Fraunces, serif" }}
          data-testid={`stay-name-${home.slug}`}
        >
          {home.shortTitle}
        </Link>

        <div className="mt-4 mx-auto h-px w-10 bg-[#D4AF37]" />

        <div className="mt-4 flex items-center justify-center gap-x-5 text-[12px] text-[#333333]">
          {home.guests ? (
            <span className="inline-flex items-center gap-1.5" data-testid={`stay-guests-${home.slug}`}>
              <Users size={13} strokeWidth={1.5} className="text-[#D4AF37]" />
              Op til {home.guests} pers.
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-[#555555]">
              <Users size={13} strokeWidth={1.5} className="text-[#D4AF37]" />
              Kontakt for antal
            </span>
          )}
        </div>

        <p className="mt-4 text-[#555555] font-light leading-relaxed text-sm flex-1 line-clamp-3">
          {home.tagline}
        </p>

        <Link
          to={href}
          data-testid={`stay-see-${home.slug}`}
          className="mt-6 w-full inline-flex items-center justify-center gap-2.5 border border-[#111111] bg-[#111111] text-white px-5 py-3.5 text-[11px] tracking-[0.22em] uppercase hover:bg-[#D4AF37] hover:border-[#D4AF37] hover:text-[#111111] transition-colors duration-500"
        >
          <span>Se bolig</span>
          <ArrowRight size={14} strokeWidth={1.5} className="transition-transform duration-500 group-hover:translate-x-1" />
        </Link>
      </div>
    </motion.article>
  );
}

export default function FeaturedStays() {
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);
  const [pages, setPages] = useState(1);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const measure = () => {
      const p = Math.max(1, Math.ceil(el.scrollWidth / el.clientWidth - 0.02));
      setPages(p);
      setActive(Math.min(p - 1, Math.round(el.scrollLeft / el.clientWidth)));
    };
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);
    return () => {
      el.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
    };
  }, []);

  const goTo = (i) => {
    const el = trackRef.current;
    if (el) el.scrollTo({ left: i * el.clientWidth, behavior: "smooth" });
  };

  return (
    <section id="boliger" data-testid="featured-stays-section" className="bg-white py-16 sm:py-28">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="text-center max-w-2xl mx-auto mb-12 sm:mb-16">
          <p className="overline mb-4 sm:mb-5 text-[10px] sm:text-xs">Vores boliger</p>
          <h2
            className="font-serif text-[2.25rem] sm:text-5xl lg:text-6xl text-[#111111] font-light tracking-tight leading-[1.05]"
            style={{ fontFamily: "Fraunces, serif" }}
          >
            Udvalgte <span className="italic text-[#C9A227]">boliger</span>.
          </h2>
          <p className="mt-5 text-base sm:text-lg text-[#555555] font-light leading-relaxed">
            Møblerede boliger i hele Danmark - til korte og længere ophold.
          </p>
        </div>

        <div className="relative">
          <div
            ref={trackRef}
            data-testid="featured-stays-track"
            className="flex gap-4 sm:gap-6 lg:gap-8 overflow-x-auto snap-x snap-mandatory no-scrollbar scroll-smooth -mx-6 px-6 sm:mx-0 sm:px-0"
          >
            {CORPORATE_HOMES.map((h, i) => (
              <ProductCard key={h.slug} home={h} index={i} />
            ))}
          </div>

          {pages > 1 && (
            <div className="mt-8 flex items-center justify-center gap-4" data-testid="featured-stays-dots">
              <button
                type="button"
                aria-label="Forrige"
                onClick={() => goTo(Math.max(0, active - 1))}
                className="p-1.5 text-[#111111]/50 hover:text-[#111111] transition-colors"
              >
                <ChevronLeft size={18} strokeWidth={1.5} />
              </button>
              {Array.from({ length: pages }).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={`Side ${i + 1}`}
                  onClick={() => goTo(i)}
                  className={`h-2.5 w-2.5 rounded-full border transition-colors duration-300 ${
                    i === active ? "bg-[#111111] border-[#111111]" : "bg-transparent border-[#111111]/40"
                  }`}
                />
              ))}
              <button
                type="button"
                aria-label="Næste"
                onClick={() => goTo(Math.min(pages - 1, active + 1))}
                className="p-1.5 text-[#111111]/50 hover:text-[#111111] transition-colors"
              >
                <ChevronRight size={18} strokeWidth={1.5} />
              </button>
            </div>
          )}
        </div>

        <div className="mt-12 sm:mt-16 flex justify-center">
          <Link to="/firmaboliger" data-testid="see-all-stays-btn" className="btn-outline-dark">
            <span className="inline-flex items-center gap-2.5">
              Se alle boliger
              <ArrowRight size={14} strokeWidth={1.5} />
            </span>
          </Link>
        </div>
      </div>
    </section>
  );
}
